<template>
    <article class="about">
        <!-- Modern Hero Section -->
    <div class="hero-section" ref="heroSection">
        <!-- Interactive background canvas -->
        <canvas class="hero-background-canvas" ref="heroCanvas"></canvas>
        
        <div class="hero-content">
            <h1 class="hero-title">
                <span class="greeting">Hello, I'm</span>
                <span class="name">Whatley Julitar</span>
            </h1>
            
            <!-- Dynamic typewriter effect -->
            <h2 class="hero-subtitle">
                <span>I'm a </span>
                <span class="typewriter" ref="typewriterElement">Software Engineer</span>
            </h2>
            
            <div class="hero-tags">
                <span class="hero-tag">Web Dev</span>
                <span class="hero-tag">Mobile Apps</span>
                <span class="hero-tag">API Integration</span>
                <span class="hero-tag">Full Stack</span>
            </div>
            
            <p class="hero-description">Turning ideas into elegant digital solutions that deliver real value</p>
            
            <div class="hero-cta">
                <a href="#services-heading" class="primary-btn">
                    <span class="btn-icon">⚙️</span> My Skills
                </a>
                <a href="#tech-skills-heading" class="secondary-btn">
                    <span class="btn-icon">🚀</span> Tech Stack
                </a>
            </div>
        </div>
        
        <div class="hero-image-container" ref="techIconsContainer">
            <div class="profile-shape"></div>
            <div class="floating-tech">
                <div class="tech-icon flutter">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg" alt="Flutter" />
                    <span class="tech-label">Flutter</span>
                </div>
                <div class="tech-icon angular">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/angularjs/angularjs-original.svg" alt="Angular" />
                    <span class="tech-label">Angular</span>
                </div>
                <div class="tech-icon spring-boot">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg" alt="Spring Boot" />
                    <span class="tech-label">Spring Boot</span>
                </div>
                <div class="tech-icon vue">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg" alt="Vue.js" />
                    <span class="tech-label">Vue.js</span>
                </div>
            </div>
        </div>
        
        <!-- Scroll indicator -->
        <div class="scroll-indicator">
            <div class="mouse">
                <div class="wheel"></div>
            </div>
            <div class="scroll-text">Scroll Down</div>
        </div>
    </div>

        <header>
            <h2 class="h2 article-title">About me</h2>
        </header>
        <section class="about-text">
            <p id="about">{{ aboutText }}</p>
        </section>
        
        <!-- Enhanced Skills Section -->
        <EnhancedSkills :services="services" />
        
        <section class="clients" aria-labelledby="tech-skills-heading">
            <div class="section-header">
                <h3 id="tech-skills-heading" class="h3 clients-title">Tech Skills & Stack</h3>
                <div class="section-divider">
                    <span class="divider-dot"></span>
                    <span class="divider-line"></span>
                    <span class="divider-dot"></span>
                </div>
                <p class="section-description">Technologies I've worked with in various projects</p>
            </div>
            
            <ul class="clients-list has-scrollbar" ref="techSkills">
                <li class="clients-item" v-for="skill in techSkills" :key="skill">
                    <a :href="skill.href" target="_blank" rel="noopener noreferrer" 
                       :aria-label="'Learn more about ' + skill.title" class="tech-card">
                        <div class="tech-icon-wrapper">
                            <img :src="getImagePathTech(skill.imgSrc)" :alt="skill.altText" loading="lazy">
                        </div>
                        <span class="client-title">{{ skill.title }}</span>
                    </a>
                </li>
            </ul>
            
            <div class="scroll-hint">
                <div class="scroll-arrow">←</div>
                <span>Scroll to see more</span>
                <div class="scroll-arrow">→</div>
            </div>
        </section>
    </article>
</template>

<script>
import { getImagePathTech } from '@/utils/images/imageLoader.js';
import { generateMetaInfo } from '@/utils/seo';
import { useHead } from '@vueuse/head';
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';
import { initTypewriterEffect, initParticleBackground, initMouseTracker, initInteractiveTechIcons } from '@/utils/animation/heroAnimations.js';
import EnhancedSkills from '../common/EnhancedSkills.vue';

export default {
    components: {
        EnhancedSkills
    },
    props: {
        aboutText: String,
        services: Array,
        techSkills: Array,
    },
    setup(props, { emit }) {
        // Refs for DOM elements
        const heroSection = ref(null);
        const heroCanvas = ref(null);
        const typewriterElement = ref(null);
        const techIconsContainer = ref(null);
        
        // Animation cleanups
        let typewriterCleanup = null;
        let particleCleanup = null;
        let mouseTrackerCleanup = null;
        
        // Log props to debug
        console.log('AboutMe props:', {
            aboutTextLength: props.aboutText ? props.aboutText.length : 0,
            services: props.services,
            techSkillsCount: props.techSkills ? props.techSkills.length : 0
        });
        
        // Colors are now handled via CSS classes
        
        // Compute SEO values that depend on props
        const seoDescription = computed(() => 
            props.aboutText 
                ? props.aboutText.substring(0, 155) + '...' 
                : 'Learn about Whatley Julitar, a Software Engineer with expertise in web and mobile app development.'
        );
        
        const seoKeywords = computed(() => 
            props.services 
                ? props.services.map(service => service.type).join(', ') + ', Software Engineer, Web Development'
                : 'Software Engineer, Web Development, Mobile Apps'
        );
        
        // Set up head metadata
        useHead(generateMetaInfo({
            title: 'About Me | Whatley Julitar',
            description: seoDescription.value,
            keywords: seoKeywords.value
        }));
        
        // Initialize animations when component is mounted
        onMounted(() => {
            // Log when component is mounted and emit event
            console.log('AboutMe component mounted, props:', {
                servicesData: props.services,
                techSkillsData: props.techSkills
            });
            
            // Emit mounted event for parent component
            emit('mounted', { services: props.services });
            
            // Start typewriter effect
            if (typewriterElement.value) {
                typewriterCleanup = initTypewriterEffect(
                    typewriterElement.value, 
                    ['Software Engineer', 'Web Developer', 'Mobile App Developer', 'Full Stack Developer']
                );
            }
            
            // Initialize particle background
            if (heroCanvas.value) {
                particleCleanup = initParticleBackground(heroCanvas.value);
            }
            
            // Add 3D tilt effect and interactive tech icons
            if (techIconsContainer.value && heroSection.value) {
                // Initialize interactive tech icons
                const interactiveTechCleanup = initInteractiveTechIcons(techIconsContainer.value);
                
                // Still add mouse tracking for 3D effect
                mouseTrackerCleanup = initMouseTracker(heroSection.value, (percentX, percentY) => {
                    // Calculate tilt angles (max 15 degrees)
                    const tiltX = (percentY - 0.5) * 15;
                    const tiltY = (0.5 - percentX) * 15;
                    
                    // Apply to floating tech icons with different intensities
                    const icons = techIconsContainer.value.querySelectorAll('.tech-icon');
                    icons.forEach((icon, index) => {
                        // Skip applying tilt if icon is being dragged
                        if (icon.classList.contains('dragging')) return;
                        
                        const intensity = 1 + (index * 0.2);
                        icon.style.transform = `perspective(1000px) rotateX(${tiltX * intensity}deg) rotateY(${tiltY * intensity}deg)`;
                    });
                });
                
                // Combine cleanup functions
                const originalCleanup = mouseTrackerCleanup;
                mouseTrackerCleanup = () => {
                    if (originalCleanup) originalCleanup();
                    if (interactiveTechCleanup) interactiveTechCleanup();
                };
            }
        });
        
        // Cleanup animations when component is unmounted
        onBeforeUnmount(() => {
            if (typewriterCleanup) typewriterCleanup();
            if (particleCleanup) particleCleanup();
            if (mouseTrackerCleanup) mouseTrackerCleanup();
        });
        
        return {
            heroSection,
            heroCanvas,
            typewriterElement,
            techIconsContainer
        };
    },
    methods: {
        getImagePathTech,
        debugServices() {
            console.log('Services data debug:', {
                props: this.$props.services,
                count: this.$props.services ? this.$props.services.length : 0,
                types: this.$props.services ? this.$props.services.map(s => s.type) : []
            });
            
            // Alert for immediate feedback
            alert('Services data status: ' + 
                  (this.$props.services && this.$props.services.length > 0 
                   ? `${this.$props.services.length} services found` 
                   : 'No services data available')
            );
        },
        startAutoScroll() {
            const techSkillsList = this.$refs.techSkills;
            if (!techSkillsList) return; // Safeguard against undefined ref
            
            // Check if we're on mobile view (using CSS media query width)
            const isMobileView = window.matchMedia('(max-width: 580px)').matches;
            
            if (!isMobileView) {
                // Auto-scroll only on non-mobile views
                this.scrollInterval = setInterval(() => {
                    techSkillsList.scrollLeft += 1; // Increment scroll
                    if (techSkillsList.scrollLeft >= techSkillsList.scrollWidth - techSkillsList.clientWidth) {
                        techSkillsList.scrollLeft = 0; // Reset to start
                    }
                }, 20); // Adjust speed by changing the interval
            }
        },
        stopAutoScroll() {
            if (this.scrollInterval) {
                clearInterval(this.scrollInterval);
            }
        },
        handleResize() {
            // Stop existing scroll and restart to handle layout changes
            this.stopAutoScroll();
            this.startAutoScroll();
        },
        setupScrollObserver() {
            // Use Intersection Observer to trigger animations when elements scroll into view
            const options = {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            };
            
            this.scrollObserver = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('animate-in');
                    }
                });
            }, options);
            
            // Observe all service items for scroll animations
            const serviceItems = document.querySelectorAll('.service-item');
            serviceItems.forEach(item => {
                this.scrollObserver.observe(item);
            });
            
            // Observe clients/tech skills section
            const clientsSection = document.querySelector('.clients');
            if (clientsSection) {
                this.scrollObserver.observe(clientsSection);
            }
        },
    },
    mounted() {
        this.$nextTick(() => {
            // Start tech skills auto-scroll
            this.startAutoScroll();
            
            // Add hero section intersection observer for scroll-based animations
            this.setupScrollObserver();
            
            // Add resize event listener to handle responsive layout changes
            window.addEventListener('resize', this.handleResize);
        });
    },
    beforeUnmount() {
        this.stopAutoScroll();
        
        // Clean up observers
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
        }
        
        window.removeEventListener('resize', this.handleResize);
    },
};
</script>


<style scoped>
/* Enhanced Hero Section Styling */
.hero-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 60px;
    background: linear-gradient(135deg, rgba(22, 22, 22, 0.95) 0%, rgba(30, 30, 30, 0.98) 100%);
    border-radius: 20px;
    padding: 50px 40px 70px;
    position: relative;
    min-height: 500px;
    overflow: hidden;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
}

/* Interactive background canvas */
.hero-background-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    opacity: 0.6;
}

.hero-content {
    flex: 1;
    z-index: 3;
    animation: fadeIn 1s ease-out;
    max-width: 60%;
}

.hero-title {
    margin: 0;
    line-height: 1.2;
}

.greeting {
    display: block;
    font-size: 22px;
    font-weight: 400;
    margin-bottom: 10px;
    color: var(--light-gray);
    opacity: 0.9;
    animation: slideInLeft 1s ease-out;
}

.name {
    display: block;
    font-size: 52px;
    font-weight: 700;
    background: var(--text-gradient-yellow);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    margin-bottom: 15px;
    text-shadow: 0 2px 10px rgba(255, 215, 108, 0.2);
    animation: slideInLeft 1s ease-out 0.2s backwards;
}

.hero-subtitle {
    font-size: 26px;
    color: white;
    margin-top: 5px;
    margin-bottom: 20px;
    animation: slideInLeft 1s ease-out 0.4s backwards;
    height: 34px; /* Fixed height to prevent layout shift during typewriter effect */
}

.typewriter {
    color: var(--orange-yellow-crayola);
    position: relative;
}

.typewriter::after {
    content: '|';
    position: absolute;
    right: -8px;
    animation: blink 0.7s infinite;
}

@keyframes blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
}

.hero-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin: 25px 0;
    animation: fadeIn 1s ease-out 0.6s backwards;
}

.hero-tag {
    background: rgba(255, 215, 108, 0.15);
    color: var(--orange-yellow-crayola);
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.hero-tag:hover {
    background: rgba(255, 215, 108, 0.25);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(255, 215, 108, 0.3);
}

.hero-description {
    color: var(--light-gray);
    margin: 20px 0 30px;
    font-size: 18px;
    line-height: 1.6;
    max-width: 90%;
    animation: fadeIn 1s ease-out 0.8s backwards;
}

.hero-cta {
    display: flex;
    gap: 20px;
    margin-top: 30px;
    animation: fadeIn 1s ease-out 1s backwards;
}

.primary-btn, .secondary-btn {
    padding: 14px 28px;
    border-radius: 10px;
    font-weight: 600;
    font-size: 16px;
    text-decoration: none;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-icon {
    font-size: 18px;
}

.primary-btn {
    background: var(--orange-yellow-crayola);
    color: var(--smoky-black);
    position: relative;
    overflow: hidden;
}

.primary-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: all 0.6s ease;
}

.primary-btn:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(255, 215, 108, 0.35);
}

.primary-btn:hover::before {
    left: 100%;
}

.secondary-btn {
    border: 2px solid var(--orange-yellow-crayola);
    color: var(--orange-yellow-crayola);
    background: transparent;
    position: relative;
}

.secondary-btn::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: var(--orange-yellow-crayola);
    transition: width 0.3s ease;
}

.secondary-btn:hover {
    background: rgba(255, 215, 108, 0.1);
    transform: translateY(-4px);
}

.secondary-btn:hover::after {
    width: 100%;
}

/* Enhanced Tech Icons */
.hero-image-container {
    position: relative;
    flex: 0 0 30%;
    height: 320px;
    z-index: 2;
}

.profile-shape {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 220px;
    height: 220px;
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    background: radial-gradient(circle at 30% 20%, rgba(255, 215, 108, 0.08) 0%, rgba(255, 215, 108, 0.03) 60%, transparent 100%);
    animation: morphShape 15s infinite alternate ease-in-out;
    box-shadow: 0 0 30px rgba(255, 215, 108, 0.1);
    z-index: -1;
}

@keyframes morphShape {
    0% {
        border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    }
    25% {
        border-radius: 70% 30% 30% 70% / 70% 70% 30% 30%;
    }
    50% {
        border-radius: 30% 30% 70% 70% / 70% 30% 70% 30%;
    }
    75% {
        border-radius: 70% 70% 30% 30% / 30% 70% 30% 70%;
    }
    100% {
        border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    }
}

.floating-tech {
    position: relative;
    width: 100%;
    height: 100%;
    transform-style: preserve-3d;
}

.tech-icon {
    position: absolute;
    width: 80px;
    height: 80px;
    background: rgba(25, 25, 25, 0.9);
    border-radius: 50%;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    cursor: pointer;
    animation: float 6s infinite ease-in-out;
}

.tech-icon img {
    width: 40px;
    height: 40px;
    margin-bottom: 6px;
}

.tech-icon::after {
    content: "";
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 2px solid var(--orange-yellow-crayola);
    opacity: 0.5;
    animation: pulse 2s infinite;
}

.tech-icon:hover {
    box-shadow: 0 12px 40px rgba(255, 215, 108, 0.25);
}

.tech-label {
    font-size: 12px;
    color: var(--orange-yellow-crayola);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.tech-icon:hover .tech-label {
    opacity: 1;
}

.flutter {
    width: 80px;
    height: 80px;
    top: 40px;
    right: 20px;
    animation-delay: 0s;
}

.angular {
    width: 90px;
    height: 90px;
    bottom: 60px;
    right: 80px;
    animation-delay: 1s;
}

.spring-boot {
    width: 75px;
    height: 75px;
    top: 150px;
    right: 10px;
    animation-delay: 2s;
}

.vue {
    width: 85px;
    height: 85px;
    top: 80px;
    right: 120px;
    animation-delay: 1.5s;
}

/* Scroll Indicator */
.scroll-indicator {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    opacity: 0.7;
    transition: opacity 0.3s ease;
    z-index: 5;
    animation: fadeIn 1s ease-out 2s backwards;
}

.scroll-indicator:hover {
    opacity: 1;
}

.mouse {
    width: 26px;
    height: 40px;
    border: 2px solid var(--orange-yellow-crayola);
    border-radius: 20px;
    position: relative;
}

.wheel {
    background: var(--orange-yellow-crayola);
    width: 4px;
    height: 8px;
    border-radius: 2px;
    position: absolute;
    top: 8px;
    left: 50%;
    transform: translateX(-50%);
    animation: scrollWheel 2s infinite;
}

.scroll-text {
    font-size: 12px;
    color: var(--light-gray);
    margin-top: 8px;
}

@keyframes scrollWheel {
    0% { 
        top: 8px;
        opacity: 1;
    }
    100% { 
        top: 24px;
        opacity: 0;
    }
}

@keyframes float {
    0%, 100% {
        transform: translateY(0);
    }
    50% {
        transform: translateY(-15px);
    }
}

@keyframes pulse {
    0% {
        transform: scale(1);
        opacity: 0.5;
    }
    50% {
        transform: scale(1.1);
        opacity: 0.8;
    }
    100% {
        transform: scale(1);
        opacity: 0.5;
    }
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes slideInLeft {
    from {
        opacity: 0;
        transform: translateX(-30px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

/* Responsive design for the hero section */
@media (max-width: 992px) {
    .hero-section {
        padding: 40px 30px 60px;
    }
    
    .hero-content {
        max-width: 90%;
    }
    
    .name {
        font-size: 46px;
    }
    
    .hero-subtitle {
        font-size: 24px;
    }
    
    /* Adjust tech icon positioning for tablets */
    .angular {
        bottom: 40px;
        right: 60px;
    }
    
    .vue {
        right: 100px;
    }
}

@media (max-width: 768px) {
    .hero-section {
        flex-direction: column;
        padding: 30px 20px 60px;
        min-height: auto;
    }
    
    .hero-content {
        max-width: 100%;
        text-align: center;
        margin-bottom: 30px;
    }
    
    .name {
        font-size: 38px;
    }
    
    .hero-subtitle {
        font-size: 22px;
        margin-bottom: 10px;
        height: 28px;
    }
    
    .hero-description {
        margin: 15px auto 25px;
        max-width: 100%;
    }
    
    .hero-tags {
        justify-content: center;
    }
    
    .hero-image-container {
        height: 180px;
        width: 100%;
    }
    
    .hero-cta {
        justify-content: center;
        gap: 15px;
    }
}

@media (max-width: 580px) {
    .hero-section {
        padding: 25px 15px 50px;
    }
    
    .name {
        font-size: 32px;
    }
    
    .greeting {
        font-size: 18px;
    }
    
    .hero-subtitle {
        font-size: 18px;
        height: 24px;
    }
    
    .hero-description {
        font-size: 16px;
    }
    
    .hero-tags {
        flex-wrap: wrap;
        justify-content: center;
    }
    
    .hero-cta {
        flex-direction: column;
        gap: 10px;
        width: 100%;
    }
    
    .primary-btn, .secondary-btn {
        display: flex;
        justify-content: center;
        width: 100%;
    }
    
    /* Adjust tech icons for mobile */
    .tech-icon {
        transform: scale(0.8);
    }
    
    .flutter {
        top: 10px;
        right: 10px;
    }
    
    .angular {
        bottom: 10px;
        right: 30px;
    }
    
    .dot-net {
        top: 60px;
        right: 5px;
    }
    
    .vue {
        top: 20px;
        right: 70px;
    }
}

/* Add styles for About section here */
.clients-list {
  max-width: 100%;
}

/* Fix for tech skills and services on smaller screens */
@media (max-width: 768px) {
  .service-list {
    grid-template-columns: 1fr;
  }
  
  .section-header {
    margin-bottom: 20px;
  }
  
  .service-item {
    padding: 20px;
  }
  
  .service-item-title {
    font-size: 18px;
  }
  
  .scroll-hint {
    font-size: 12px;
  }
}

@media (max-width: 580px) {
  /* Improved service items */
  .service-item {
    padding: 15px;
    margin-bottom: 15px;
  }
  
  .service-item-badge {
    top: 15px;
    right: 15px;
    font-size: 10px;
    padding: 3px 8px;
  }
  
  .service-icon-box {
    margin-bottom: 15px;
  }
  
  /* Improved tech skills grid */
  .clients-list.has-scrollbar {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    overflow-x: hidden;
    white-space: normal;
  }
  
  .clients-item {
    width: 100%;
  }
  
  .tech-card {
    padding: 12px 8px;
  }
  
  .tech-icon-wrapper {
    width: 40px;
    height: 40px;
  }
  
  .client-title {
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .scroll-hint {
    display: none; /* Hide scroll hint on mobile grid view */
  }
}

/* Ensure clients section doesn't break layout on any screen size */
/* Enhanced Section Headers */
.section-header {
  margin-bottom: 30px;
  text-align: center;
}

.section-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 15px 0;
}

.divider-line {
  width: 60px;
  height: 2px;
  background: var(--orange-yellow-crayola);
  opacity: 0.7;
}

.divider-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--orange-yellow-crayola);
  margin: 0 8px;
}

.section-description {
  color: var(--light-gray);
  font-size: 16px;
  margin-top: 15px;
  opacity: 0.9;
}

/* Enhanced Service Items */
.service-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 25px;
}

.service-item {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 15px;
  padding: 25px;
  transition: all 0.4s ease;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  z-index: 1; /* Ensure z-index is set for proper layering */
}

/* Color-coded service types */
.service-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  opacity: 0.7;
  transition: all 0.3s ease;
  z-index: -1;
}

/* Web development - blue theme */
.service-item.web-dev::before {
  background: linear-gradient(to bottom, #4aa3df, rgba(74, 163, 223, 0.5));
}

/* Mobile apps development - green theme */
.service-item.mobile-dev::before {
  background: linear-gradient(to bottom, #2ecc71, rgba(46, 204, 113, 0.5));
}

/* Desktop apps development - purple theme */
.service-item.desktop-dev::before {
  background: linear-gradient(to bottom, #9b59b6, rgba(155, 89, 182, 0.5));
}

/* API development - orange theme (default) */
.service-item.api-dev::before {
  background: linear-gradient(to bottom, var(--orange-yellow-crayola), rgba(255, 215, 108, 0.5));
}

.service-item:hover::before {
  width: 100%;
  opacity: 0.05;
}

.service-item:hover {
  transform: translateY(-5px) !important;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  border-color: rgba(255, 255, 255, 0.1);
}

.service-item:hover .service-icon-box {
  transform: scale(1.1);
}

.service-icon-box {
  position: relative;
  background: var(--eerie-black-1);
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.service-item:hover .service-icon-box {
  transform: translateY(-5px);
}

/* Different color shadows for each service type */
.web-dev .service-icon-box {
  box-shadow: 0 5px 15px rgba(74, 163, 223, 0.2);
}

.mobile-dev .service-icon-box {
  box-shadow: 0 5px 15px rgba(46, 204, 113, 0.2);
}

.desktop-dev .service-icon-box {
  box-shadow: 0 5px 15px rgba(155, 89, 182, 0.2);
}

.api-dev .service-icon-box {
  box-shadow: 0 5px 15px rgba(255, 215, 108, 0.2);
}

.icon-bg-glow {
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 215, 108, 0.1);
  filter: blur(10px);
  opacity: 0;
  transition: opacity 0.3s ease;
}

/* Different color glows for each service type */
.web-dev .icon-bg-glow {
  background: rgba(74, 163, 223, 0.15);
}

.mobile-dev .icon-bg-glow {
  background: rgba(46, 204, 113, 0.15);
}

.desktop-dev .icon-bg-glow {
  background: rgba(155, 89, 182, 0.15);
}

.api-dev .icon-bg-glow {
  background: rgba(255, 215, 108, 0.15);
}

.service-item:hover .icon-bg-glow {
  opacity: 1;
}

.service-item-title {
  margin-bottom: 15px;
  font-size: 20px;
  line-height: 1.3;
  transition: color 0.3s ease;
}

/* Different color titles for each service type */
.web-dev .service-item-title {
  color: #4aa3df;
}

.mobile-dev .service-item-title {
  color: #2ecc71;
}

.desktop-dev .service-item-title {
  color: #9b59b6;
}

.api-dev .service-item-title {
  color: var(--orange-yellow-crayola);
}

.service-item-text {
  font-size: 16px;
  line-height: 1.6;
  color: var(--light-gray);
}
</style>
